// client/src/pages/resource/ReservationCalendar.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ResourcePicker from './ResourcePicker.js';

import { AuthContext } from '../../context/AuthContext.js';
import { getReservations, getReservationById } from '../../api/resourceMngApi.js';
import { deleteReservation, approveReservation, rejectReservation } from '../../api/resourceMngApi';

import ReservationCreate from './ReservationCreate.js';
import ReservationView from './ReservationView.js';

import '../../css/calendar.css';
import '../../pages/resource/Reservation.css';

// AM/PM 포맷 함수
function formatAMPM(date) {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  minutes = minutes < 10 ? '0' + minutes : minutes;
  return `${hours}:${minutes} ${ampm}`;
}

function mapStatus(status) {
  switch (status) {
    case 'approve':
      return '승인';
    case 'pending':
      return '대기';
    case 'reject':
      return '반려';
    case 'cancel':
      return '취소';
    case 'active':                // ✅ 추가
      return '승인/대기';          // UI용 그룹 라벨
    default:
      return status;
  }
}

function ReservationCalendar() {
  // robust: 모든 훅을 최상단에 선언
  const location = useLocation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedRange, setSelectedRange] = useState(null); // 예약 작성/수정용
  const [selectedReservation, setSelectedReservation] = useState(null); // 예약 조회용(일정 패턴)
  const [isReadOpen, setIsReadOpen] = useState(false); // 예약 조회 모달 오픈(일정 패턴)
  const { token, user } = useContext(AuthContext);
  const lastFetchKey = useRef('');
  const [currentParams, setCurrentParams] = useState({ viewtype: 'month' });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [resourceId, setResourceId] = useState(null);
  const [resourceName, setResourceName] = useState('');
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status');
  const calendarRef = useRef(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [targetReservation, setTargetReservation] = useState(null);
  const [rejectMode, setRejectMode] = useState("create"); // 추가: 작성/조회 모드 분기

  // 예약 작성/수정 모달 닫기 (selectedRange용)
  const closeModal = () => {
    setSelectedRange(null);
    // robust: 닫을 때 viewId 쿼리 파라미터가 있으면 제거 (수정 취소 시 등 대응)
    const params = new URLSearchParams(location.search);
    if (params.has('viewId')) {
      params.delete('viewId');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  };

  // viewId 쿼리로 진입 시 robust 모달 자동 오픈 (일정 패턴)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('viewId');
    if (!viewId) return;
    if (events.length === 0) {
      return;
    }
    const found = events.find(ev => String(ev.id) === String(viewId));
    if (found && found.extendedProps && found.extendedProps.reservation) {
      setSelectedReservation(found.extendedProps.reservation);
      setIsReadOpen(true);
    } else if (!found) {
      // robust: 리스트에 없으면 단건 상세조회 시도
      (async () => {
        try {
          const res = await getReservationById(viewId, token);
          if (res && res.id) {
            setSelectedReservation(res);
            setIsReadOpen(true);
          } else {
            alert('존재하지 않는 예약입니다.');
            // 쿼리스트링에서 viewId 제거
            params.delete('viewId');
            navigate({ pathname: location.pathname, search: params.toString() ? '?' + params.toString() : '' }, { replace: true });
          }
        } catch (err) {
          alert('존재하지 않는 예약입니다.');
          params.delete('viewId');
          navigate({ pathname: location.pathname, search: params.toString() ? '?' + params.toString() : '' }, { replace: true });
        }
      })();
    }
  }, [location.search, location.pathname, events, token, navigate]);

  const handleDatesSet = (dateInfo) => {
    const viewTypeMap = {
      dayGridMonth: 'month',
      timeGridWeek: 'week',
      timeGridDay: 'day'
    };

    const viewtype = viewTypeMap[dateInfo.view.type] || 'month';

    // 시작/종료 날짜 (YYYY-MM-DD)
    const startDate = dateInfo.startStr.split('T')[0];
    const endDate = dateInfo.endStr.split('T')[0];

    // 년/월/일 추출
    const start = new Date(dateInfo.start);
    const year = start.getFullYear();
    const month = start.getMonth() + 1;
    const date = startDate; // 일 뷰일 경우 사용

    setCurrentParams({
      viewtype,
      year,
      month,
      startDate,
      endDate,
      date
    });
  };

  const handleRefresh = (force = false) => {
    if (force) {
      lastFetchKey.current = '';
      fetchReservations();
    } else {
      fetchReservations();
    }
  };

  // ✅ 예약 목록 불러오기
  const fetchReservations = useCallback(async () => {
    if (!token || !user?.userid) return;

    const currentKey = `${status}-${token}-${user?.userid}-${currentParams.viewtype}-${currentParams.startDate}-${currentParams.endDate}-${currentParams.date}-${resourceId}`;
    if (lastFetchKey.current === currentKey) return;

    try {
      const data = await getReservations(
        currentParams.viewtype,
        { status, ...currentParams, resourceId },
        token
      );

      const reservations = Array.isArray(data) ? data : data?.reservations || [];
      let filtered = reservations;
      const role = String(user?.role).toLowerCase();

      if (role === 'admin') {
        filtered = reservations;
      } else {
        if (status === 'approve') {
          filtered = reservations.filter(res => res.status === '승인');
        } else if (status === 'active') {
          filtered = reservations.filter(res => res.status === '승인' || res.status === '대기');
        } else if (status === 'pending') {
          filtered = reservations.filter(
            res => res.writerId === user?.userid || res.approverId === user?.userid
          );
        } else if (status === 'reject' || status === 'cancel') {
          filtered = reservations.filter(
            res => res.writerId === user?.userid || res.approverId === user?.userid
          );
        }
      }

      // robust: viewId가 있으면 status/필터와 무관하게 해당 예약을 events에 반드시 포함
      const params = new URLSearchParams(location.search);
      const viewId = params.get('viewId');
      let viewReservation = null;
      if (viewId) {
        viewReservation = reservations.find(res => String(res.id) === String(viewId));
        if (viewReservation && !filtered.some(res => String(res.id) === String(viewId))) {
          filtered = [viewReservation, ...filtered];
        }
      }

      // 진단용 로그: reservations에 viewId 예약이 실제로 있는지 확인
      console.log('[진단] reservations:', reservations.map(r => r.id), 'viewId:', viewId);
      // id-forcing robust: id에 주요 정보(제목, 시간 등) 포함
      const mapped = filtered.map(res => {
        const start = new Date(`${res.resStartDate}T${res.resStartTime}`);
        let end = new Date(`${res.resEndDate}T${res.resEndTime}`);
        const timeLabel = formatAMPM(start);
        const isMultiDay = res.resStartDate !== res.resEndDate;

        if (isMultiDay && end instanceof Date && !isNaN(end)) {
          end.setDate(end.getDate() + 1);
        }

        // 주요 정보가 바뀌면 id도 바뀌게 만듦 (DOM/툴팁 강제 재생성)
        const eventId = `${res.id}_${encodeURIComponent(res.subject)}_${encodeURIComponent(res.resStartDate)}_${encodeURIComponent(res.resStartTime)}_${encodeURIComponent(res.resEndDate)}_${encodeURIComponent(res.resEndTime)}`;

        return {
          id: eventId,
          title: status === 'active'
            ? `${res.status === '승인' ? '✅' : '⏳'} (${timeLabel}) [${res.resName}] ${res.subject}`
            : `(${timeLabel}) [${res.resName}] ${res.subject}`,
          start,
          end,
          color: res.resColor,
          allDay: isMultiDay,
          extendedProps: {
            reservation: res,
            statusLabel: mapStatus(res.status)
          }
        };
      });

      setEvents(mapped);
      lastFetchKey.current = currentKey;

      // robust: viewId 쿼리 있으면 fetch 후 바로 모달 오픈 (reservation이 있을 때만)
      // (예약 조회 모달은 selectedReservation/isReadOpen으로만 관리)
    } catch (err) {
      console.error("[ReservationCalendar] 예약 목록 불러오기 실패:", err);
      setEvents([]);
    }
  }, [status, token, user?.userid, user?.role, currentParams, resourceId, location.search]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const handleSelect = (info) => {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0,5);

    if (info.view.type === 'dayGridMonth') {
      setSelectedRange({
        start: `${info.startStr}T${currentTime}`,
        end: `${info.endStr}T${currentTime}`,
        actionType: 'select'
      });
    } else {
      setSelectedRange({
        start: info.startStr,
        end: info.endStr,
        actionType: 'select'
      });
    }
  };

  const handleCreateClick = () => {
    const now = new Date();
    const isoNow = now.toISOString();
    setSelectedRange({
      start: isoNow,
      end: isoNow,
      actionType: 'select'
    });
  };

  // 예약 조회 모달 닫기 (일정 패턴)
  const closeReadModal = () => {
    setIsReadOpen(false);
    setSelectedReservation(null);
    const params = new URLSearchParams(location.search);
    if (params.has('viewId')) {
      params.delete('viewId');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  };

  const getTitle = () => {
    if (status === 'approve') return '예약 승인 현황';
    if (status === 'pending') return '예약 대기 현황';
    if (status === 'reject') return '예약 반려 조회';
    if (status === 'cancel') return '예약 취소 조회';
    if (status === 'active') return '예약 승인/대기 현황'; // ✅ 추가
    return '예약';
  };


   // ✅ UI 렌더링
  return (
    <div className="reservation-calendar container-fluid py-4">
      <div className="card shadow-sm">
        <div className="card-body">
          {/* 상단 타이틀 및 버튼 */}
          <div className="d-flex justify-content-between align-items-center mb-3 p-3 rounded shadow"
            style={{ background: 'linear-gradient(90deg, #607d8b 0%, #cfd8dc 100%)', color: '#fff' }}>
            {/*<h4 className="mb-0 fw-bold">{getTitle()}</h4>*/}
            <h4
              className="mb-0 fw-bold d-flex align-items-center"
              onClick={() => {
                setResourceId(null);
                setResourceName('');
                fetchReservations();
              }}
              style={{ cursor: 'pointer' }}
            >
              <span>{getTitle()}</span>
              {resourceName && (
                <span style={{ fontSize: '0.85em', marginLeft: '6px' }}>
                  [{resourceName}]
                </span>
              )}
            </h4>
             <div className="text-end">
              <div className="btn-group gap-1">
                {/* 대상 선택 */}
                <button className="btn btn-primary btn-sm" onClick={() => setPickerOpen(true)}>
                  조회 대상 선택
                </button>

                {/* 전체 보기 */}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setResourceId(null);
                    setResourceName('');
                    fetchReservations();
                  }}
                >
                  전체 보기
                </button>

                {/* 예약 등록 (강조 + 간격 벌리기) */}
                <button
                  className="btn btn-success btn-sm fw-bold ms-3"  // ✅ ms-2로 왼쪽 간격 추가
                  onClick={handleCreateClick}
                >
                  <i className="bi bi-plus-circle"></i> 예약 등록
                </button>
              </div>
            </div>

          </div>

          <hr className="divider" />          
          {/* FullCalendar 설정 */}
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            locale="ko"
            selectable={true}
            selectMirror={true}
            slotEventOverlap={false}/*시간대별 이벤트가 겹칠 때의 레이아웃을 결정*/
            select={handleSelect}
            events={events} 
            headerToolbar={{
              left: 'prev,next',
              center: 'title todayBtn',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            footerToolbar={{
              left: 'prev,next',
              center: 'title todayBtn',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}

            ref={calendarRef}
            customButtons={{
              todayBtn: {
                text: '오늘',
                click: () => {
                  const calendarApi = calendarRef.current.getApi();
                  calendarApi.today();
                },
              },
            }}
            height="auto"
            eventDisplay="block"
            themeSystem="bootstrap5"
            showNonCurrentDates={false}
            fixedWeekCount={false}
            dayMaxEventRows={false}
            datesSet={handleDatesSet}
            allDaySlot={true}
            dayMaxEvents={true} /*더보기 링크: 월간 뷰에서 일정이 너무 많을 때 "+N개"로 숨김true(높이맞춤), 숫자, false(무제한)*/
            expandRows={true}
            slotDuration="00:20:00"
            selectAllow={(selectInfo) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const startDate = new Date(selectInfo.start);
              startDate.setHours(0, 0, 0, 0);
              return startDate >= today;
            }}
            dayCellDidMount={(arg) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const cellDate = new Date(arg.date);
              cellDate.setHours(0, 0, 0, 0);
              if (cellDate < today) {
                arg.el.style.backgroundColor = '#f0f0f0';
              }
            }}
            eventContent={(arg) => {
              const bgColor = arg.event.backgroundColor || arg.event.color || '#6c757d';
              return {
                html: `<div style="text-align:left; background-color:${bgColor}; color:#fff; border-radius:4px; padding:0px 4px;">
                  ${arg.event.title}
                </div>`
              };
            }}
            eventDidMount={(info) => {
              // robust: Tooltip 인스턴스와 모든 관련 속성 제거 후 재생성
              if (window.bootstrap && window.bootstrap.Tooltip) {
                const tooltipInstance = window.bootstrap.Tooltip.getInstance(info.el);
                if (tooltipInstance) tooltipInstance.dispose();
              }
              info.el.removeAttribute('data-bs-original-title');
              info.el.removeAttribute('aria-label');
              info.el.removeAttribute('title');
              // title을 강제로 최신값으로 재설정
              info.el.setAttribute('title', info.event.title);
              if (window.bootstrap) {
                setTimeout(() => {
                  new window.bootstrap.Tooltip(info.el, {
                    title: info.event.title,
                    placement: 'top',
                    trigger: 'hover',
                    container: 'body'
                  });
                }, 0);
              }
            }}
            eventClick={(info) => {
              setSelectedReservation(info.event.extendedProps.reservation);
              setIsReadOpen(true);
            }}
            
            eventMouseEnter={(info) => {
              // 부모(Harness) 박스에 클래스 추가
              info.el.parentElement?.classList.add('event-hover-active');
            }}
            eventMouseLeave={(info) => {
              // 부모(Harness) 박스에서 클래스 제거
              info.el.parentElement?.classList.remove('event-hover-active');
            }}
          />
        </div>
      </div>

      {/* 예약 작성 모달 */}
      {selectedRange && selectedRange.actionType === 'select' && createPortal(
        <div className="reservation-modal">
          <div className="reservation-modal-content">
            <div className="reservation-modal-body">
              <ReservationCreate
                defaultDate={selectedRange.start}
                defaultEndDate={selectedRange.end}
                actionType={selectedRange.actionType}
                onClose={closeModal}
                onRefresh={() => handleRefresh(true)}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* 예약 조회 모달 (일정 패턴) */}
      {isReadOpen && selectedReservation && createPortal(
        <div className="reservation-modal">
          <div className="reservation-modal-content">
            <div className="reservation-modal-header">
              <h3 className="modal-title">예약 조회</h3>
              <button type="button" className="btn-close" onClick={closeReadModal}></button>
            </div>
            <div className="reservation-modal-body">
              <ReservationView
                reservation={selectedReservation}
                actions={{
                  edit: (res) => {
                    setIsReadOpen(false);
                    setTimeout(() => setSelectedRange({ reservation: res, actionType: 'edit' }), 0);
                  },
                  delete: async (res) => {
                    if (window.confirm("삭제하시겠습니까?")) {
                      await deleteReservation(res.id, token);
                      alert("삭제되었습니다.");
                      handleRefresh(true);
                      closeReadModal();
                    }
                  },
                  approve: async (res) => {
                    if (window.confirm("승인하시겠습니까?")) {
                      await approveReservation(res.id, token);
                      alert("승인되었습니다.");
                      handleRefresh(true);
                      closeReadModal();
                    }
                  },
                  reject: (res) => {
                    setTargetReservation(res);
                    setRejectMode("create");
                    setRejectReason("");
                    setShowRejectModal(true);
                  },
                  viewReject: (res) => {
                    setTargetReservation(res);
                    setRejectReason(res.rejectReason || "");
                    setRejectMode("read");
                    setShowRejectModal(true);
                  },
                  close: closeReadModal
                }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 예약 수정 모달 */}
      {selectedRange && selectedRange.actionType === 'edit' && createPortal(
        <div className="reservation-modal">
          <div className="reservation-modal-content">
            <div className="reservation-modal-body">
              <ReservationCreate
                reservation={selectedRange.reservation}
                actionType={selectedRange.actionType}
                onClose={closeModal}
                onRefresh={() => {
                  // robust: viewId 쿼리 정리하여 저장 후 재오픈 방지
                  const params = new URLSearchParams(location.search);
                  if (params.has('viewId')) {
                    params.delete('viewId');
                    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
                  }
                  handleRefresh(true);
                }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 반려 모달 */}
      {showRejectModal && createPortal(
        <>
          <div
            className="modal-backdrop show"
            style={{ zIndex: 3000, backgroundColor: "rgba(128,128,128,0.5)" }}
          ></div>

          <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 3050 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {rejectMode === "create" ? "반려 사유 작성" : "반려 의견 조회"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowRejectModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <label htmlFor="rejectReason" className="form-label fw-bold text-danger">
                    반려 의견
                  </label>
                  <textarea
                    id="rejectReason"
                    className="form-control"
                    rows="4"
                    value={rejectReason}
                    onChange={(e) => rejectMode === "create" && setRejectReason(e.target.value)}
                    placeholder={rejectMode === "create" ? "반려 사유를 입력하세요" : ""}
                    style={{ resize: "none", overflowY: "auto" }}
                    readOnly={rejectMode === "read"}
                  />
                </div>
                <div className="modal-footer">
                  {rejectMode === "create" ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={async () => {
                          if (!rejectReason.trim()) {
                            alert("반려 사유를 입력하세요.");
                            return;
                          }
                          await rejectReservation(targetReservation.id, rejectReason, token);
                          alert("반려되었습니다.");
                          setShowRejectModal(false);
                          setRejectReason("");
                          setTargetReservation(null);
                          handleRefresh(true);
                          closeModal();
                        }}
                      >
                        반려
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowRejectModal(false)}
                      >
                        취소
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowRejectModal(false)}
                    >
                      닫기
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* 자원 찾기 모달 */}
      {pickerOpen && createPortal(
        <ResourcePicker
          onSelect={(res) => {
            setResourceId(res.id);
            setResourceName(res.res_name);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />,
        document.body
      )}
      
    </div>
  );
}

export default ReservationCalendar;
